import { inject, Injectable } from '@angular/core';
import { User } from '@shared-library/models/users/user';
import { UserOrganization } from '@shared-library/models/users/user-organization';
import { UserRole } from '@shared-library/models/users/user-role';
import { UsersService } from '@shared-library/services/users/user.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import OrganisationType from '@shared-library/models/users/org-type';
import { AirNavPaymentProviderService } from '@back-office/app/air-nav-payment-provider/air-nav-payment-provider.service';
import { OperatorsService } from '@back-office/app/operators/operator.service';
import { Operator } from '@back-office/app/operators/operator';
// Import other necessary services like OperatorService, AirNavPaymentProviderService if used elsewhere.
// Assume they are correctly imported if needed by methods not shown in persistence logic.

const LS_USER_KEY = 'authUser';
const LS_ROLES_KEY = 'authRoles';
const LS_ORGS_KEY = 'authOrgs';
const LS_CURRENT_ORG_ID_KEY = 'authCurrentOrgId';
const LS_CURRENT_ORG_TYPE_KEY = 'authCurrentOrgType';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly usersService = inject(UsersService);
  // Inject other services like OperatorsService, AirNavPaymentProviderService as needed
  private readonly operatorsService = inject(OperatorsService);
  private readonly airNavPaymentProviderService = inject(AirNavPaymentProviderService);

  private userSubject = new BehaviorSubject<User | null>(this.loadFromLocalStorage<User>(LS_USER_KEY));
  private rolesSubject = new BehaviorSubject<UserRole[]>(this.loadFromLocalStorage<UserRole[]>(LS_ROLES_KEY) || []);
  private organizationsSubject = new BehaviorSubject<UserOrganization[]>(this.loadFromLocalStorage<UserOrganization[]>(LS_ORGS_KEY) || []);
  private currentOrgSubject = new BehaviorSubject<UserOrganization | null>(null);

  user$ = this.userSubject.asObservable();
  roles$ = this.rolesSubject.asObservable();
  organizations$ = this.organizationsSubject.asObservable();
  currentOrg$ = this.currentOrgSubject.asObservable();

  private initialFetchCompleted = false;

  constructor() {
    this.setInitialCurrentOrgFromStorage();
  }

  private loadFromLocalStorage<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) as T : null;
    } catch (error) {
      console.error(`Error reading ${key} from localStorage`, error);
      localStorage.removeItem(key);
      return null;
    }
  }

  private saveToLocalStorage<T>(key: string, data: T): void {
    try {
      if (data === null || data === undefined) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(data));
      }
    } catch (error) {
      console.error(`Error saving ${key} to localStorage`, error);
    }
  }

  fetchUser(backOffice?: boolean): Observable<User | null> {
    console.log('Fetching user data...');
    // clear roles
    // localStorage.removeItem(LS_ROLES_KEY);
    // localStorage.removeItem(LS_ORGS_KEY);
    return this.usersService.getAuthUser().pipe(
      tap(user => {
        const currentUser = this.userSubject.getValue();
        const currentUserId = currentUser?.id;
        const newUserId = user?.id;
        // If the user has changed, clear all cached state first
        if (currentUserId && newUserId && currentUserId !== newUserId) {
          this.clearAuthState();
        }
        // Update the user and roles in the BehaviorSubjects
        this.userSubject.next(user);
        const roles = user?.extras?.userRoles || [];
        const organizations = user?.extras?.userOrganizations || [];
        this.rolesSubject.next(roles);
        this.organizationsSubject.next(organizations);

        this.saveToLocalStorage(LS_USER_KEY, user);
        this.saveToLocalStorage(LS_ROLES_KEY, roles);
        this.saveToLocalStorage(LS_ORGS_KEY, organizations);

        this.setInitialCurrentOrgFromStorage({ backOffice });

        this.initialFetchCompleted = true;
      }),
      catchError(error => {
        console.error('Error fetching user (likely requires login):', error);
        this.clearAuthState();
        this.initialFetchCompleted = true;
        return of(null);
      })
    );
  }

  private setInitialCurrentOrgFromStorage({ backOffice }: { backOffice?: boolean; } = {}): void {
    const organizations = this.organizationsSubject.getValue();
    const storedOrgId = this.loadFromLocalStorage<string>(LS_CURRENT_ORG_ID_KEY);
    let initialOrg: UserOrganization | null = null;

    if (storedOrgId) {
      initialOrg = organizations.find(org => org.organizationId === storedOrgId) || null;
    }

    if (!initialOrg && !backOffice) {
      initialOrg = organizations.find(org => org.organizationId !== OrganisationType.BACK_OFFICE) || null;
      this.setCurrentOrg(initialOrg?.organizationId || '');
    } else {
      if (backOffice) {
        initialOrg = organizations.find(org => org.organizationType === OrganisationType.BACK_OFFICE || org.organizationId === OrganisationType.BACK_OFFICE) || null;
      }
      this.setCurrentOrg(initialOrg?.organizationId || '');
    }

    this.currentOrgSubject.next(initialOrg);
  }

  setCurrentOrg(orgId: string): void {
    const organizations = this.organizationsSubject.getValue();
    const selectedOrg = organizations.find(org => org.organizationId === orgId);
    if (selectedOrg) {
      this.currentOrgSubject.next(selectedOrg);
      this.saveToLocalStorage(LS_CURRENT_ORG_ID_KEY, selectedOrg.organizationId);
      this.saveToLocalStorage(LS_CURRENT_ORG_TYPE_KEY, selectedOrg.organizationType);
    } else {
      console.warn('Organization not found for setCurrentOrg:', orgId);
    }
  }

  clearAuthState(): void {
    this.userSubject.next(null);
    this.rolesSubject.next([]);
    this.organizationsSubject.next([]);
    this.currentOrgSubject.next(null);

    localStorage.removeItem(LS_USER_KEY);
    localStorage.removeItem(LS_ROLES_KEY);
    localStorage.removeItem(LS_ORGS_KEY);
    localStorage.removeItem(LS_CURRENT_ORG_ID_KEY);
    localStorage.removeItem(LS_CURRENT_ORG_TYPE_KEY);

    this.initialFetchCompleted = false;
  }

  getCurrentOrg(): UserOrganization | null {
    return this.currentOrgSubject.getValue();
  }

  // --- Other methods from original code ---
  // Add back other methods like getOperators, hasRole etc. if they are needed,
  // ensuring they don't rely on services not injected above.

  getOperators(): Observable<Operator[]> {
    // Ensure operatorsService is injected if this method is used
    return this.operatorsService.getDashboardOperators().pipe(
      catchError(error => {
        console.error('Error fetching operators:', error);
        return of([]);
      })
    );
  }


  hasRole(requiredRoles: string | string[]): boolean {
    const currentOrg = this.getCurrentOrg();

    if (!currentOrg) return false;

    const roles = this.rolesSubject.getValue();
    const rolesToCheck = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];

    return rolesToCheck.some(requiredRole =>
      roles.some(role =>
        `${role.organisationType}*${role.role}` === requiredRole
        && role.organisationId === currentOrg.organizationId
      )
    );
  }

  getFormattedRoles(): { formattedRole: string; organisationId: string | undefined }[] {
    return this.rolesSubject.getValue().map(role => ({
      formattedRole: `${role.organisationType}*${role.role}`,
      organisationId: role.organisationId,
    }));
  }

  getCurrentOrgDetails(): UserOrganization | null {
    return this.getCurrentOrg();
  }

  getNonBackOfficeUserOrgs(): UserOrganization[] {
    const organizations = this.organizationsSubject.getValue();
    return organizations.filter(org => org.organizationId !== OrganisationType.BACK_OFFICE);
  }


  getAllUserOrgs(): UserOrganization[] {
    return this.organizationsSubject.getValue();
  }

  getUser(): User | null {
    return this.userSubject.getValue();
  }

  getRoles(): UserRole[] {
    return this.rolesSubject.getValue();
  }
}
